import { ChangeDetectionStrategy, Component, OnDestroy, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { BehaviorSubject, map } from 'rxjs';

import {
	AlarisCardComponent,
	AlarisDialogService,
	AlarisLoadingDirective,
	AlarisPaginationComponent,
	AlarisCellContextDirective,
} from '@campaign-portal/components-library';
import { MnpTable } from '@helpers/types/table/mnp-table';
import { Source, HlrCurrencies } from '@helpers/types/models';
import { UserService } from '../../auth/user.service';
import { CurrenciesService } from '../settings/currencies/currencies.service';
import { TableManagementComponent } from '@helpers/components/table-management/table-management.component';

import { SourcesService } from './sources.service';
import { SourcesFieldsService } from './sources.fields.service';
import { SourcesDeleteDialogComponent } from './dialog/dialog.component';
import { ProvidersService } from '@helpers/repo/providers.service';
import { DsEmptyStateComponent } from '@alarislabs/angular-components/empty-state';
import { DsButtonComponent } from '@alarislabs/angular-components/button';
import { DsNoDataFoundComponent } from '@alarislabs/angular-components/no-data-found';
import { DsTableComponent } from '@alarislabs/angular-components/table';
import { DsInputComponent } from '@alarislabs/angular-components/input';
import { DsIconSwapComponent } from '@alarislabs/angular-components/icon-swap';
import { DsDropdownPresetAction, DsMultiSelectDropdownPresetComponent } from '@alarislabs/angular-components/dropdown';
import { DsFilterItemComponent } from '@alarislabs/angular-components/filter-item';
import { EntityNotificationService } from '@helpers/services/entity-notification.service';

@Component({
	selector: 'app-sources',
	templateUrl: './sources.component.html',
	styleUrls: [
		'./sources.component.scss',
		// eslint-disable-next-line max-len
		'../../../../node_modules/@campaign-portal/components-library/src/assets/templates/table-complex/complex-table.component.scss',
	],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		AlarisCardComponent,
		AlarisLoadingDirective,
		AlarisPaginationComponent,
		AlarisCellContextDirective,
		TableManagementComponent,
		DsTableComponent,
		DsButtonComponent,
		DsInputComponent,
		DsIconSwapComponent,
		DsMultiSelectDropdownPresetComponent,
		AsyncPipe,
		TranslateModule,
		ReactiveFormsModule,
		DsNoDataFoundComponent,
		DsEmptyStateComponent,
		DsFilterItemComponent,
	],
})
export class SourcesComponent extends MnpTable<Source> implements OnDestroy {
	override pageSizeOptions = [25, 50, 100, 200];
	readonly exporting$ = new BehaviorSubject<boolean>(false);

	readonly connectionTypeOptions = ['ENUM', 'HTTP', 'SS7', 'MNP', 'CAMARA', 'AI'];

	filtersForm = new FormGroup({
		name: new FormControl(''),
		type: new FormControl<string[]>([]),
		connectionType: new FormControl<string[]>([]),
		huntStop: new FormControl(''),
		currency: new FormControl<HlrCurrencies[]>([]),
	});

	readonly dropdownActions: DsDropdownPresetAction<Source>[] = [
		{
			leftIconName: 'icon-edit-20',
			label: 'actions.edit',
			onClick: this.edit.bind(this),
			disabled: () => !this.user.allowed(['sources.edit']),
		},
		{
			leftIconName: 'icon-delete-20',
			label: 'actions.delete',
			onClick: this.delete.bind(this),
			disabled: () => !this.user.allowed(['sources.edit']),
		},
	];

	private entityNotification = inject(EntityNotificationService);
	private readonly dialog = inject(AlarisDialogService);
	private readonly router = inject(Router);
	protected readonly user = inject(UserService);
	public readonly currenciesService = inject(CurrenciesService);
	private readonly providersService = inject(ProvidersService);
	readonly sourcesService = inject(SourcesService);

	readonly currencies = toSignal(this.currenciesService.list$.pipe(map((list) => (list ?? []).map((c) => c.name))), {
		initialValue: [] as string[],
	});
	readonly typeOptions = toSignal(
		this.providersService.list$.pipe(map((list) => [...new Set((list ?? []).map((p) => p.name))])),
		{ initialValue: [] as string[] }
	);

	constructor() {
		super(inject(SourcesService), inject(SourcesFieldsService));

		this.currenciesService.readActive().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();

		this.providersService.loadProviders('').pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
	}

	ngOnDestroy(): void {
		this.exporting$.next(false);
		this.exporting$.complete();
	}

	edit(source?: Source): void {
		if (source) {
			this.sourcesService.saveToSession(source);
			this.router.navigate(['/sources/edit', source.sourceId]);
		} else {
			this.sourcesService.clearSession();
			this.router.navigate(['/sources/new']);
		}
	}

	delete(source: Source): void {
		this.dialog
			.open<boolean>(SourcesDeleteDialogComponent, {
				autoFocus: false,
				data: { source },
			})
			.closed.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((success) => {
				if (success) {
					this.entityNotification.showSuccess('sources.source', 'deleted');
					this.reload();
				}
			});
	}

	export(): void {
		this.exporting$.next(true);
		this.sourcesService
			.export(this.total, this.getReadParamsMnp())
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(() => this.exporting$.next(false));
	}

	pageChange(event: { pageIndex: number; pageSize: number }): void {
		this.sourcesService.pageSize = event.pageSize;
		this.sourcesService.activePage = event.pageIndex;
		this.reload();
	}

	override isFiltersApplied(): boolean {
		return Object.values(this.filtersForm.value).some((v) => v && (Array.isArray(v) ? v.length > 0 : true));
	}

	resetFilters(): void {
		this.filtersForm.reset({
			name: '',
			type: [],
			connectionType: [],
			huntStop: '',
			currency: [],
		});
		this.reload();
	}

	getCurrencyDisplayName(data: Source): string {
		if (data.connectionType === 'CAMARA') {
			return '-';
		}
		if (data.currencyName) {
			return data.currencyName;
		}
		const systemCurrency = this.currenciesService.list$.value.find((c) => c.isSystemCurrency);
		return systemCurrency?.name ?? '-';
	}
}
